// --- Enum e tipi base ---
export enum UserRole {
    ADMIN = 'ADMIN',
    RESPONSABILE = 'RESPONSABILE',
    COLLABORATORE = 'COLLABORATORE'
}

export interface User {
    id: number;
    name: string;
    surname: string;
    email: string;
    role: UserRole;
    confirmed?: boolean;
    sedeId?: number;
    sedeIds?: number[];
    sediAccessibili?: number[];
    // Sedi di appartenenza: dove l'utente lavora.
    sedi?: { id: number; name: string }[];
    // Sedi di cui è responsabile (sottoinsieme delle sedi di appartenenza).
    // Vuoto per collaboratori e titolare.
    sediGestiteIds?: number[];
    whatsappOptIn?: boolean;
    // True se l'istanza ha WhatsApp attivo: solo allora il profilo mostra opt-in e telefono.
    whatsappEnabled?: boolean;
    // Numero a cui inviare le notifiche WhatsApp, modificabile dal profilo.
    phone?: string;
}

export interface AuthState {
    user: User | null;
    isAuthenticated: boolean;
    currentSedeId: number | 'ALL' | null;
}

export interface ApiResponse<T> {
    success: boolean;
    message: string;
    data: T;
}

export interface AuthResponse {
    token: string;
    id: number;
    email: string;
    name: string;
    surname: string;
    role: string;
    // Per il responsabile è la prima sede gestita.
    sedeId?: number;
    sedi?: { id: number; name: string }[];
    sediGestiteIds?: number[];
}

export interface LoginRequest {
    email: string;
    password: string;
}

// --- Dominio ---

export type PraticaStato = 'In Corso' | 'Sospesa' | 'Chiusa' | 'Pianificato';

export interface Pratica {
    id: string | number;
    nome?: string;          // usato dal frontend
    name?: string;          // usato dal backend

    clientName?: string;    // nome cliente inviato dal backend
    clientId?: number;      // ID del cliente collegato

    descrizione?: string;
    cliente: string;        // campo legacy
    stato: PraticaStato | string;
    assegnatari: string[];
    assegnatariNomi?: string[];
    ultimaAttivita: string;
    citta: string;
    sedeId?: number;
    note?: string;
    createdAt?: string;

    // Il backend non invia l'oggetto completo: spesso è undefined
    client?: Client[];
    files?: Files[];

    // Tipo di mandato (mand-investigativo / prev-bonifiche / ...): condiziona la UI,
    // es. il download "Soggetto investigato" solo per i mandati investigativi.
    modelId?: string;

    // Dati cliente esposti direttamente dal DTO
    clientEmail?: string;
    clientTelefono?: string;

    // Firma digitale OTP Service
    /** ID dossier OTP del Mandato inviato a firma */
    otpMandatoDossierId?: number;
    /** ID dossier OTP dell'Informativa Privacy inviata a firma */
    otpPrivacyDossierId?: number;
    /**
     * Stato del processo di firma:
     *   undefined/null    → nessuna firma avviata
     *   'IN_ATTESA_FIRMA' → firma avviata, in attesa che il cliente firmi
     *   'MANDATO_FIRMATO' → mandato firmato, privacy ancora in attesa
     *   'SOGGETTO_FIRMATO' → dati soggetto firmati, mandato ancora in attesa ('PRIVACY_FIRMATA' è il nome precedente)
     *   'FIRMATA'         → entrambi i documenti firmati
     */
    otpFirmaStatus?: 'IN_ATTESA_FIRMA' | 'MANDATO_FIRMATO' | 'SOGGETTO_FIRMATO' | 'PRIVACY_FIRMATA' | 'FIRMATA' | null;
    /**
     * Codice di verifica firma del dossier Mandato (`final_checksum` di OTP Service),
     * verificabile pubblicamente su https://app.otpservice.io/signature.
     */
    otpFinalChecksumMandato?: string | null;
    /** Come sopra, per il dossier Soggetto Investigato. */
    otpFinalChecksumSoggetto?: string | null;

    /**
     * Etichetta libera del mandato (città, punto vendita, soprannome). Mostrata al
     * posto di "Città" nella tabella /pratiche, modificabile solo da titolare/responsabile.
     */
    riferimento?: string | null;
    /** Valorizzato quando i dati del soggetto investigato sono stati svuotati (restano le iniziali). */
    soggettoAnonimizzatoAt?: string | null;

    /**
     * Flag per i badge GDPR:
     *   - `clienteDeleted=true` → cliente eliminato, riapertura mandato bloccata
     *   - `clienteAnonymized=true` → cliente anonimizzato: il nome arriva già come
     *     "XXXXXX", il badge spiega il perché.
     */
    clienteDeleted?: boolean;
    clienteAnonymized?: boolean;
}

export interface PraticaDto extends Pratica {}

// Cliente: unifica i nomi di campo usati da backend e frontend
// Riepilogo attività di un cliente (GET /clients/riepilogo-attivita).
// Slot di turno da controllare e turno di un altro collaboratore già presente sul servizio.
export interface SlotTurno {
    dataTurno: string;   // "YYYY-MM-DD"
    oraInizio: string;   // "HH:mm"
    oraFine: string;     // "HH:mm"
    notturno?: boolean;
}
export interface Compresenza {
    turnoId: number;
    collaboratore: string;
    data: string;
    oraInizio: string;
    oraFine: string;
    notturno: boolean;
    richiestaData?: string;
    richiestaOraInizio?: string;
    richiestaOraFine?: string;
}

export interface ClienteAttivita {
    mandatiInvestigativi: number;
    mandatiBonifica: number;
    mandatiConsulenza: number;
    servizi: number;
    // Mandati non conclusi o servizi fiduciari non terminati.
    lavoriAperti: boolean;
    // Mesi con attività, 'YYYY-MM' in ordine crescente.
    mesi: string[];
    // Ultimo giorno di attività già trascorso, 'YYYY-MM-DD'.
    ultimaAttivita: string | null;
}

export interface Client {
    id?: number | string;

    // Identificazione
    nome: string;
    name?: string;         // a volte arriva dal backend come 'name'

    surname?: string;
    email: string;
    telefono: string;

    // Tipo cliente
    tipo: 'Privato' | 'Azienda' | 'Legale';
    type?: string;         // il backend può inviare 'PRIVATE' o 'COMPANY'

    note?: string;

    // Indirizzi
    indirizzo?: string;
    citta?: string;
    cap?: string;
    provincia?: string;

    // Dati Fiscali
    codiceFiscale?: string;
    partitaIva?: string;
    codiceSdi?: string;
    pec?: string;

    // Dati Aziendali
    ragioneSociale?: string;
    companyName?: string;  // alias di ragioneSociale lato backend

    sedeLegaleVia?: string;
    sedeLegaleCitta?: string;
    sedeLegaleCap?: string;
    sedeLegaleProv?: string;

    // Dati Persona Fisica
    luogoNascita?: string;
    dataNascita?: string;
    docTipo?: string;
    docNumero?: string;
    docRilascioDa?: string;
    docDataRilascio?: string;
    docDataScadenza?: string;

    // Referente / firmatario (per cliente Azienda).
    indirizzoReferente?: string;
    cittaReferente?: string;
    capReferente?: string;
    provinciaReferente?: string;
    emailReferente?: string;
    telefonoReferente?: string;

    // Per conto di terzi
    perContoDiTerzi?: boolean;
    denominazioneTerzi?: string;
    sedeTerzi?: string;
    capTerzi?: string;
    comuneTerzi?: string;
    provinciaTerzi?: string;
    pivaOrCfTerzi?: string;
    codiceSdiTerzi?: string;
    emailTerzi?: string;

    // Sistema
    sedeId?: number;
    createdAt?: string;
    sede?: Sede[];
    pratiche?: Pratica[];

    // Statistiche
    praticheAttive?: number;
    praticheTotali?: number;
}

export interface ClientDto extends Client {}
export type Cliente = Client;

export interface Collaboratore {
    id: number;
    nome: string;
    ruolo: string;
    email: string;
    stato: 'Attivo' | 'Inattivo';
    sedeId?: number;
    ruoloAccesso?: 'ADMIN' | 'RESPONSABILE' | 'COLLABORATORE';
}

export interface MediaItem {
    id: string;
    type: 'image' | 'video';
    url: string;
    caption: string;
    date: string;
    timestamp: string;
}

export interface CampoDinamico {
    key: string;
    label: string;
    tipo: 'text' | 'number' | 'date' | 'textarea' | 'currency';
    placeholder?: string;
}

export interface ModelloDocumento {
    id: string;
    nome: string;
    descrizione: string;
    tipo: 'Preventivo' | 'Mandato' | 'Informativa';
    campiRichiesti: CampoDinamico[];
}

// --- DTO backend ---

export interface UserDto extends User {}

export interface Company {
    id?: number;
    name: string;
    note: string;
    sedi: Sede[];
    deleted: boolean;
    deletedAt: string;
}
export interface CompanyDto extends Company {}

export interface Sede {
    id: number;
    name: string;
    address: string;
    note?: string;
    praticheAttive: number;
    collaboratoriAttivi?: number;
    responsabileName?: string;
    responsabileId?: number;
    // Responsabili della sede, cioè gli utenti che la gestiscono (non chi vi lavora
    // solo come collaboratore). Ricalcolati dal server. Preferire a responsabileName.
    responsabili?: Array<{ id: number; name: string }>;
    citta?: string;
    cap?: string;

    // --- Configurazione WhatsApp ---
    whatsappProvider: 'META' | 'TWILIO' | 'NONE';

    // Meta (Cloud API)
    whatsappPhoneId?: string;
    // Token in sola scrittura: il backend non lo restituisce mai. Inviarlo solo per
    // aggiornarlo (stringa vuota = invariato); per la UI usare `whatsappTokenSet`.
    whatsappToken?: string;
    whatsappTokenSet?: boolean;

    // Twilio
    twilioAccountSid?: string;
    // Stesso comportamento di whatsappToken.
    twilioAuthToken?: string;
    twilioAuthTokenSet?: boolean;
    twilioPhoneNumber?: string;
}

export interface CreateSedeRequest {
    name: string;
    address: string;
    note?: string;

    responsabileMode: 'EXISTING' | 'NEW_INVITE';
    responsabileId?: number;
    newResponsabileEmail?: string;
}

export interface CreateSedeResponse {
    sede: Sede;
    inviteLink?: string;
    inviteToken?: string;
}

export interface SedeDto extends Sede {
    inviteLink?: string;
    inviteToken?: string;
}

export interface Mandato { id?: number; }
export interface MandatoDto extends Mandato {}

export interface Documents { id?: number; name: string; company: Company[]; }
export interface DocumentsDto extends Documents {}

export interface Files {
    id?: number;
    name: string;
    size: number;
    extension: string;
    createdAt: string;

    // Campi di FilesDto
    urlOriginal?: string;
    urlCompressed?: string;
    keyOriginal?: string;
    keyCompressed?: string;
    status?: string;            // 'COMPLETED', 'IN_PROGRESS', ...
    errorMessage?: string;

    // Campi legacy / relazioni
    key?: string;               // legacy
    pratica?: Pratica[];
}
export interface FilesDto extends Files {}

// --- Preventivi ---

export type PreventivoStato = 'BOZZA' | 'INVIATO' | 'ACCETTATO' | 'RIFIUTATO';

export interface VocePreventivo {
    id: string;
    descrizione: string;
    quantita: number;
    unita: 'ore' | 'km' | 'forfait' | 'n.';
    prezzoUnitario: number;
    scontoPercentuale: number;
    totale?: number;
}

export interface Preventivo {
    id: number | string;
    numeroProgressivo: string;
    dataCreazione: string;
    stato: PreventivoStato;

    cliente?: Client;
    clienteId?: number | string;
    sedeId?: number;

    voci: VocePreventivo[];
    /** Imponibile (€), valore autoritativo inserito dall'utente. */
    imponibile: number;
    /** Aliquota IVA in percentuale (es. 22). */
    ivaPercentuale: number;
    /** Cache server-side dell'IVA (€). */
    ivaValore: number;
    /** Cache server-side del totale ivato (€), esposto come "totale". */
    totale: number;
    /**
     * Campo derivato dal backend: riferimento da mostrare in fattura,
     * contabilità e lista pagamenti.
     */
    totaleIvato?: number;

    noteInterne?: string;
    notePerCliente?: string;

    // Campi temporanei per nuovi contatti
    tempClientName?: string;
    tempClientEmail?: string;
    tempClientPhone?: string;

    // Collegamento al mandato, valorizzato dal backend quando il preventivo
    // viene convertito in mandato.
    praticaId?: number;
    praticaNome?: string;

    // tipoDocumento parte come 'PREVENTIVO' e diventa 'PROFORMA' al primo upload
    // del PDF proforma. Include PDF persistito e flag fattura elettronica.
    tipoDocumento?: 'PREVENTIVO' | 'PROFORMA';
    pdfKey?: string;
    pdfUrl?: string;
    fatturaElettronicaEmessa?: boolean;
    numeroFatturaElettronica?: string;
    createdAt?: string;

    // Badge cliente (eliminato/anonimizzato).
    clienteDeleted?: boolean;
    clienteAnonymized?: boolean;
}

export interface PreventivoDto extends Preventivo {}

// Payload per l'invio API WhatsApp
export interface SendWhatsappRequest {
    phoneNumber: string;
    message: string;
    attachPdf?: boolean;
}

export interface ScadenzaDto {
    id: number;
    titolo: string;
    note?: string;
    dataScadenza?: string; // ISO string
    completed: boolean;
    // Autore: popolati lato BE per ADMIN/RESPONSABILE che vedono note di altri.
    // Sulle note proprie il FE li riconosce e nasconde la riga "Da: ...".
    userId?: number;
    userFullName?: string;
    userRole?: 'ADMIN' | 'RESPONSABILE' | 'COLLABORATORE';
    sedeNome?: string;
}

export interface SettingsDto {
    id?: number;
    priceKM: number;
    hourlyRate?: number;
    enableProforma?: boolean;
    headerReportInvestigativo?: string;
    footerReportInvestigativo?: string;

    headerType?: 'TEXT' | 'IMAGE';
    footerType?: 'TEXT' | 'IMAGE';

    headerImageUrl?: string;
    footerImageUrl?: string;

    costSetupPractice?: number;

    hourlyRateFestivo?: number;
    hourlyRateNotturno?: number;

    majDistance50100?: number;
    majDistance100150?: number;
    majDistance150250?: number;
    // Fascia "oltre 250 km" (default 75%): senza, oltre i 250 km la maggiorazione sarebbe 0.
    majDistance250Plus?: number;

    majNight?: number;
    majHoliday?: number;
    majNightHoliday?: number;

    /** Fascia oraria notturna (HH:mm), usata per le maggiorazioni automatiche nel preventivo. */
    orarioNotturnoInizio?: string;
    orarioNotturnoFine?: string;

    majUrgency48?: number;
    majUrgency24?: number;
    majUrgency12?: number;

    majReport48?: number;
    majReport24?: number;

    openAiApiKey?: string;
    // Parametri di "Migliora relazione".
    openAiModel?: string;
    openAiTemperature?: number;
    openAiTopP?: number;
    openAiPresencePenalty?: number;
    openAiFrequencyPenalty?: number;
    openAiMaxTokens?: number;
    openAiSystemPrompt?: string;
    openAiDeveloperPrompt?: string;

    otpServiceApiKey?: string;
    otpServiceEmail?: string;
    otpServicePassword?: string;
    // Secret dei webhook OTP Service: si scrive soltanto; il flag dice se è salvato.
    otpServiceWebhookSecret?: string;
    otpServiceWebhookSecretRimuovi?: boolean;
    otpServiceWebhookSecretSet?: boolean;

    agencyName?: string;
    address?: string;
    city?: string;
    phone?: string;
    email?: string;
    vatNumber?: string;
    website?: string;

    // Usati nei placeholder dei documenti legali.
    pec?: string;
    licenzaPrefettizia?: string;
    emailDpo?: string;

    // Coordinate bancarie per il blocco "Modalità di pagamento" della proforma.
    iban?: string;
    intestatario?: string;
    bancaNome?: string;

    logoUrl?: string;
    reportHeaderUrl?: string;
    reportFooterUrl?: string;
    /** URL immagine header per Mandati e Preventivi (da S3, generato dal backend) */
    mandatoHeaderUrl?: string;
    /** URL immagine footer per Mandati e Preventivi (da S3, generato dal backend) */
    mandatoFooterUrl?: string;
    /** URL locale dopo upload (prima del salvataggio) */
    mandatoHeaderImageUrl?: string;
    mandatoFooterImageUrl?: string;
    /** URL presignato del timbro agenzia (in basso a destra nell'ultima pagina della relazione). */
    timbroUrl?: string;
    /** URL presignato del PDF Informativa Privacy (da S3) */
    privacyPolicyUrl?: string;
    /**
     * Sorgente dell'Informativa Privacy:
     *   'UPLOAD' (default) → usa il PDF caricato (privacyPolicyUrl)
     *   'AUTO'             → genera dinamicamente con i dati dell'agenzia (InformativaPrivacyPDF)
     */
    privacyPolicyMode?: 'UPLOAD' | 'AUTO';

    /**
     * Soglia in minuti per l'allerta "Orari difformi" sui turni fiduciari.
     * Se omessa l'allerta non scatta.
     */
    sogliaOrariDifformiMin?: number;

    /**
     * Soglia separata per gli incarichi investigativi, dove gli orari sono più
     * flessibili. Null/0/omessa: nessuna allerta.
     */
    sogliaOrariDifformiIncarichiMin?: number;

    /**
     * Se true il responsabile elimina i media direttamente come l'admin; altrimenti
     * serve l'approvazione del titolare. Il collaboratore passa sempre dall'approvazione.
     */
    responsabilePuoEliminareMediaDiretto?: boolean;

    /**
     * Se true il collaboratore vede in /le-mie-spese anche i buoni pasto auto generati
     * dal sistema. In ogni caso non può modificarli né eliminarli.
     */
    buoniPastoVisibiliCollaboratori?: boolean;

    /**
     * Tolleranza in minuti tra l'EXIF DateTimeOriginal della foto di start/stop e
     * il momento dell'azione: fuori tolleranza o senza EXIF il backend risponde 422.
     * Null = default 15, 0 = controllo disattivato.
     */
    tolleranzaExifFotoStartStopMin?: number;

    /**
     * Abilita l'anonimizzazione notturna automatica dei dati cestinati.
     * Default false: nessun job GDPR agisce finché il titolare non lo attiva.
     */
    gdprAutoAnonymizationEnabled?: boolean;

    /**
     * Giorni di retention prima dell'anonimizzazione dei record cestinati.
     * Null = 180, 0 = al primo ciclo notturno.
     */
    gdprRetentionDays?: number;

    /**
     * Retention del job di sicurezza per gli utenti staff cancellati (null = 3650).
     * La cancellazione pseudonimizza già subito: questo copre solo i record vecchi.
     */
    gdprUserRetentionDays?: number;

    /**
     * Giorni dopo l'anonimizzazione oltre i quali cliente / servizio fiduciario viene
     * eliminato fisicamente. Null = mai. Incarichi, spese e turni restano orfani per lo storico.
     */
    gdprHardDeleteDaysAfterAnonymization?: number;

    /**
     * Soglia in MB oltre la quale i video non vengono controllati per duplicati
     * (niente SHA-256). Null = 200, 0 = nessun limite. Le foto sono sempre controllate.
     */
    dedupVideoMaxMb?: number;
}

export interface ActivityType {
    id?: number;
    name: string;
    description: string;
    defaultPrice?: number;
    sortOrder?: number;
}

export interface WorkSession {
    id: number;
    startTime: string;
    startLat?: number;
    startLon?: number;
    endTime?: string;
}

export interface FinancialStats {
    fatturato: number;
    incassato: number;
    spese: number;
    utile: number;
    trend: number;
    daIncassare?: number;
    // Entrate una tantum: già comprese in incassato (e in fatturato se fatturate).
    entrateUnaTantum?: number;
    // Uscite una tantum: NON comprese in spese, vanno sommate ai costi a parte.
    usciteUnaTantum?: number;
}

export interface MovimentoContabile {
    id: number;
    data: string; // ISO Date
    descrizione: string;
    praticaRef?: string;
    sedeNome: string;
    importo: number;
    tipo?: 'entrata' | 'uscita' | string;
    // Distingue movimenti di mandato da quelli di servizio fiduciario; MANUALE sono le
    // entrate/uscite una tantum dell'agenzia.
    entityType?: 'PRATICA' | 'SERVIZIO' | 'MANUALE';
    // Campi del registro movimenti; sull'endpoint /amministrazione/movimenti restano undefined.
    sourceKey?: string;
    orphan?: boolean;
    // Spesa o compenso di un report senza più collaboratore né mandato/servizio: si elimina dal registro.
    eliminabile?: boolean;
    category?:
        | 'ACCONTO_MANDATO'
        | 'MOVIMENTO_MANDATO_ENTRATA'
        | 'MOVIMENTO_MANDATO_USCITA'
        | 'MOVIMENTO_SERVIZIO_ENTRATA'
        | 'MOVIMENTO_SERVIZIO_USCITA'
        | 'SPESA_COLLABORATORE_EXTRA'
        | 'SPESA_COLLABORATORE_REPORT'
        | 'MOVIMENTO_MANUALE_ENTRATA'
        | 'MOVIMENTO_MANUALE_USCITA'
        | string;
    // Flag "fattura elettronica emessa": solo per le entrate legate a un movimento
    // di mandato o servizio, altrimenti undefined.
    fatturato?: boolean;
}

// Filtri per /amministrazione/registro-movimenti.
export type RegistroMovimentiPreFilter = 'ALL' | 'PRATICHE' | 'SERVIZI';
export interface RegistroMovimentiFilters {
    sedeId?: number | 'ALL';
    from?: string;   // ISO yyyy-MM-dd
    to?: string;     // ISO yyyy-MM-dd
    preFilter?: RegistroMovimentiPreFilter;
}

// Payload di PATCH /registro-movimenti/{sourceKey}: si applicano solo i campi valorizzati.
export interface MovimentoEditRequest {
    data?: string;        // ISO yyyy-MM-dd
    descrizione?: string;
    importo?: number;
    tipo?: string;        // "entrata"|"uscita" per mandati; "INCASSO"|"RIMBORSO"|... per servizi
    fatturato?: boolean;  // solo incassi (ledger mandati eliminati); "fattura emessa"
}

// Entrata o uscita una tantum dell'agenzia (conta solo alla sua data).
export interface MovimentoManuale {
    id: number;
    sedeId: number;
    data: string;         // ISO yyyy-MM-dd
    voce: string;
    importo: number;      // sempre positivo, il segno lo dà il tipo
    tipo: 'entrata' | 'uscita';
    fatturato?: boolean;  // solo entrate
}
export interface MovimentoManualePayload {
    sedeId: number;
    data: string;
    voce: string;
    importo: number;
    tipo: 'entrata' | 'uscita';
    fatturato?: boolean;
}

// Riga dello storico variazioni delle spese fisse.
export interface SpesaFissaAuditRow {
    id: number;
    spesaFissaId?: number | null;
    sedeId?: number | null;
    voce?: string;
    importo?: number;
    action: 'INSERT' | 'DELETE' | string;
    at: string;           // ISO LocalDateTime
    byUserName?: string;
}

export interface PraticaNonSaldata {
    id: number;
    nome: string;
    rif: string;
    clienteNome: string;
    stato: string;
    importoTotale: number;
    importoIncassato: number;
    importoResiduo: number;

    /**
     * Debito di fatturazione, indipendente dall'incasso:
     * `importoDaFatturare = importoTotale − importoFatturato`. Un mandato pagato
     * senza fattura ha `importoResiduo = 0` e `importoDaFatturare` pieno.
     */
    importoFatturato?: number | null;
    importoDaFatturare?: number | null;

    dataCreazione: string;
    /**
     * Quando il mandato è uscito dai KPI (archiviazione o eliminazione). Null per
     * le righe ordinarie e per gli archiviati senza data nota, che compaiono in ogni periodo.
     */
    dataUscita?: string | null;

    /**
     * Motivo dell'uscita dai KPI. Sulle righe `ELIMINATO` il backend non valorizza
     * `nome`, `rif` e `clienteNome` perché la delete anonimizza i dati.
     */
    motivoUscita?: 'ARCHIVIATO' | 'SOSPESO' | 'ANNULLATO' | 'ELIMINATO' | null;

    /**
     * `PRATICA` (default) o `SERVIZIO`. Per i servizi nome e cliente restano
     * valorizzati (non vengono anonimizzati).
     */
    entityType?: 'PRATICA' | 'SERVIZIO';
}

/** Cosa si perde eliminando un mandato. */
export interface PreflightEliminazione {
    haResiduo: boolean;
    /** True se resta un importo mai fatturato. */
    haDaFatturare: boolean;
    importoTotale: number;
    importoIncassato: number;
    importoResiduo: number;
    importoFatturato: number;
    importoDaFatturare: number;
    /** Report giornalieri da approvare o da rifare sugli incarichi del mandato. */
    reportInAttesa?: number;
    /** Spese in attesa di approvazione sugli incarichi del mandato. */
    speseInAttesa?: number;
    /** Valorizzato se ci sono report o spese in attesa: l'eliminazione viene rifiutata. */
    messaggioBlocco?: string | null;
}

/** Verifica prima di eliminare un servizio fiduciario. */
export interface PreflightEliminazioneServizio {
    /** Report giornalieri da approvare o da rifare sui turni del servizio. */
    reportInAttesa: number;
    /** Spese in attesa di approvazione sui turni del servizio. */
    speseInAttesa: number;
    /** Valorizzato se ci sono report o spese in attesa: l'eliminazione viene rifiutata. */
    messaggioBlocco?: string | null;
}

export interface PraticaNonFatturata {
    id: number;
    rif: string;
    nome: string;
    clienteNome: string;
    stato: string;
    importoTotale: number;
    importoFatturato: number;
    importoDaFatturare: number;
    dataConclusione?: string;
}

// Equivalenti per i servizi fiduciari.
export interface ServizioNonSaldato {
    id: number;
    nome: string;
    clienteNome: string;
    stato: string;
    importoTotale: number;
    importoIncassato: number;
    importoResiduo: number;
}

export interface ServizioNonFatturato {
    id: number;
    nome: string;
    clienteNome: string;
    stato: string;
    importoTotale: number;
    importoFatturato: number;
    importoDaFatturare: number;
}


export interface IncaricoMedia {
    id: number;
    filename: string;
    fileType: string;
    note?: string;
    uploadTime: string;
    latitude?: number;
    longitude?: number;
    status: string;
    // Campi restituiti dal DTO per i link
    finalUrl?: string;
    urlCompressed?: string;
    // Stato effettivo del watermark e scelta fatta all'upload: TabMedia li usa
    // per decidere se mostrare il toggle.
    watermarkApplied?: boolean;
    userWantsWatermark?: boolean;
    dataAcquisizioneFromExif?: boolean;
}

export interface IncaricoNote {
    id: number;
    text: string;
    createdAt: string;
    lat?: number;
    lon?: number;
    // Autore della nota.
    author?: User;
}

export interface Incarico {
    id: number;
    pratica: Pratica;
    collaboratore?: User;
    stato: 'ASSEGNATO' | 'ACCETTATO' | 'IN_CORSO' | 'COMPLETATO' | 'REVOCATO' | 'RIFIUTATO';
    // Momento in cui il collaboratore ha accettato.
    dataAccettazione?: string;
    // Scadenza opzionale per l'accettazione: oltre, il backend porta l'incarico in RIFIUTATO.
    dataScadenzaAccettazione?: string;
    // Momento della revoca, separato da dataFine che conserva la fine pianificata.
    dataRevoca?: string;
    // Valorizzato in stato RIFIUTATO (rifiuto manuale o per scadenza).
    motivoRifiuto?: string;

    dataInizio?: string;
    dataFine?: string;
    luogo?: string;
    note?: string;
    soggettoNome?: string;

    activeSessionId?: number;

    // Origine dell'incarico:
    // NORMALE: flusso standard
    // AUTOGENERATO: contenitore per upload tardivi
    // URGENTE: aperto dal collaboratore dopo aver inviato il report del giorno
    // URGENTE_SENZA_MANDATO: aperto senza mandato (pratica=null) finché il
    //   titolare non lo abbina da /approvazioni.
    tipoOrigine?: 'NORMALE' | 'AUTOGENERATO' | 'URGENTE' | 'URGENTE_SENZA_MANDATO';
    inseritoAPosteriori?: boolean;

    // True se per oggi esiste già una relazione giornaliera: il frontend blocca
    // Start/Stop e propone l'incarico urgente.
    reportInviatoOggi?: boolean;
    /** Incarico unico a cavallo della mezzanotte, con un solo report datato al giorno di inizio. */
    continuativo?: boolean;

    // Array aggiuntivi dal DTO
    sessioni?: WorkSession[];
    media?: IncaricoMedia[];
    noteList?: IncaricoNote[];

    relazioni?: any[];
}

export interface FiguraProfessionale {
    id?: number;
    nome: string;
    descrizione: string;
    tariffa: number;        // costo interno (pagato al collaboratore)
    prezzoCliente?: number; // prezzo esposto al cliente nel preventivo
}

// --- Analisi e approvazione media ---

export interface FileAnalysisResultDto {
    // Campi del flusso originale
    tempId: string;
    status: 'ready' | 'needs_date';
    date?: string;
    // Campi di analisi
    mediaId?: number;
    name?: string;
    analysisStatus: 'OK' | 'NO_EXIF' | 'OUTSIDE_RANGE' | 'DUPLICATE' | 'PENDING_CALLBACK' | 'ERROR';
    exifDate?: string;        // ISO string
    duplicateOfId?: number;
    duplicateOfName?: string;
    // Mostrato solo per i media orfani (provenienti da incarichi diversi).
    incaricoLabel?: string;
    // Anteprima per gli orfani: URL presigned e MIME per scegliere tra <img> e placeholder video.
    thumbnailUrl?: string;
    fileType?: string;
}

export interface PendingApprovalItem {
    id: number;
    mediaId?: number;
    mediaName: string;
    // DELETION_REQUEST: il collaboratore chiede l'eliminazione (approva = elimina,
    // rifiuta = mantiene). MANUAL_DATE: data proposta per un media senza EXIF.
    reason: 'OUTSIDE_RANGE' | 'DUPLICATE' | 'DELETION_REQUEST' | 'MANUAL_DATE';
    submittedByName: string;
    submittedAt: string;
    incaricoRef: string;
    thumbnailUrl?: string;
    praticaId?: number;
    praticaName?: string;
    // Per OUTSIDE_RANGE: data EXIF e periodo pianificato servono al titolare per
    // decidere al volo. Per gli altri motivi possono mancare.
    dataAcquisizione?: string;      // ISO LocalDateTime
    incaricoDataInizio?: string;
    incaricoDataFine?: string;
    // true = data da EXIF della fotocamera (probante); false = inserita a mano
    // dal collaboratore; undefined = non noto.
    dataAcquisizioneFromExif?: boolean;
    // Data proposta dal collaboratore (ISO LocalDateTime).
    proposedDate?: string;
}

// Scostamento tra orario reale e pianificato, per incarichi o turni.
// `tipo` indica quale tra incaricoId e turnoId è valorizzato; `mandatoNome`
// è il mandato per gli incarichi e il servizio per i turni.
export type AllertDifformitaTipo = 'INCARICO' | 'TURNO';

export interface AllertOrariDifformi {
    id: number;
    tipo?: AllertDifformitaTipo;
    incaricoId?: number;
    turnoId?: number;
    mandatoNome?: string;
    collaboratoreId: number;
    collaboratoreNome?: string;
    dataPianificata: string;       // ISO YYYY-MM-DD
    scostamentoInizioMin: number;
    scostamentoFineMin?: number | null;
    sogliaMin: number;
    // Orari pianificati vs reali (null sui record vecchi)
    inizioPianificato?: string | null;
    inizioReale?: string | null;
    finePianificata?: string | null;
    fineReale?: string | null;
    dataRilevazione: string;       // ISO datetime
}

// Start/stop di un turno fuori dal raggio configurato sul servizio fiduciario.
export interface AllertGeofence {
    id: number;
    turnoId?: number;
    servizioId?: number;
    servizioNome?: string;
    collaboratoreId?: number;
    collaboratoreNome?: string;
    tipoEvento: 'START' | 'STOP';
    latRilevata: number;
    lngRilevata: number;
    distanzaMt: number;
    centroLatSnapshot: number;
    centroLngSnapshot: number;
    raggioMtSnapshot: number;
    createdAt: string; // ISO datetime
}

// Foto arrivata dalla coda offline con EXIF assente o fuori tolleranza.
// Non blocca il collaboratore: il titolare la verifica a posteriori.
export type AllertMediaTipo = 'INCARICO_MEDIA' | 'TURNO_FOTO_START' | 'TURNO_FOTO_STOP';
export type AllertMediaAnomalia = 'EXIF_MISSING' | 'EXIF_OUT_OF_TOLERANCE';

export interface AllertMedia {
    id: number;
    tipoMedia: AllertMediaTipo;
    tipoAnomalia: AllertMediaAnomalia;
    incaricoId?: number;
    incaricoMediaId?: number;
    categoria?: string;          // "KM" per le foto contachilometri
    turnoId?: number;
    servizioId?: number;
    servizioNome?: string;
    collaboratoreId?: number;
    collaboratoreNome?: string;
    filename?: string;
    expectedTimestamp?: string;  // ISO datetime dichiarato dal client
    exifTimestamp?: string;      // ISO datetime letto dall'EXIF
    diffSeconds?: number;
    tolleranzaMinSnapshot?: number;
    createdAt: string;           // ISO datetime
}

// Wrapper Page di Spring Data (solo i campi usati).
export interface SpringPage<T> {
    content: T[];
    totalElements: number;
    totalPages: number;
    number: number;                // pagina corrente 0-based
    size: number;
}